import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Header from "../components/Header";
import FellowshipCard from "../components/FellowshipCard";
import Footer from "../components/Footer";
import { Newspaper } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function Home({ theme, toggleTheme }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [region, setRegion] = useState("all");
  const [openOnly, setOpenOnly] = useState("all");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ categories: [], regions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/opportunities"), api.get("/opportunities/meta")])
      .then(([oppsRes, metaRes]) => {
        setItems(oppsRes.data);
        setMeta(metaRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((it) => {
        if (category !== "all" && it.category !== category) return false;
        if (region !== "all" && it.region !== region) return false;
        if (openOnly === "open" && it.status === "CLOSED") return false;
        if (!q) return true;
        return (
          it.title.toLowerCase().includes(q) ||
          it.description.toLowerCase().includes(q) ||
          it.org.toLowerCase().includes(q) ||
          it.tags.some((t) => t.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => b.votes - a.votes);
  }, [items, query, category, region, openOnly]);


  return (
    <div className="App min-h-screen bg-background">
      <Header
        query={query}
        setQuery={setQuery}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      <main className="mx-auto max-w-[1000px] px-4">
        <div className="flex justify-end pt-4">
          <a
            href="https://buildermantra.substack.com/archive"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="substack-archive-link"
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-brand"
          >
            <Newspaper className="h-3.5 w-3.5" /> Read our latest newsletter
          </a>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-3 py-5 sm:flex-row sm:flex-wrap sm:items-center">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 w-full rounded-md border border-border bg-secondary/40 text-sm text-foreground sm:w-[150px]">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {meta.categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="h-9 w-full rounded-md border border-border bg-secondary/40 text-sm text-foreground sm:w-[150px]">
              <SelectValue placeholder="All regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All regions</SelectItem>
              {meta.regions.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={openOnly} onValueChange={setOpenOnly}>
            <SelectTrigger className="h-9 w-full rounded-md border border-border bg-secondary/40 text-sm text-foreground sm:w-[130px]">
              <SelectValue placeholder="Open only" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open only</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-[13px] text-muted-foreground sm:ml-auto">
            {filtered.length} results
          </span>
        </div>

        {/* List */}
        <div className="pb-24">
          {loading ? (
            <div className="py-24 text-center text-muted-foreground">Loading opportunities...</div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center text-muted-foreground">
              No opportunities match your filters.
            </div>
          ) : (
            filtered.map((item, i) => (
              <FellowshipCard key={item.id} item={item} index={i} />
            ))
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
